import { Controller } from '@hotwired/stimulus'

// Creates a parallax effect where the footer slides up over page content as user scrolls
export default class extends Controller {
  connect() {
    this.handleScroll = this.handleScroll.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.handleWheel = this.handleWheel.bind(this)
    this.handleFooterWheel = this.handleFooterWheel.bind(this)
    this.handleWheelForFooter = this.handleWheelForFooter.bind(this)
    this.handleFooterScroll = this.handleFooterScroll.bind(this)
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)

    // Use container div as scroll container
    this.scrollElement = document.querySelector('#container') || window
    const target = this.scrollElement === window ? window : this.scrollElement
    target.addEventListener('scroll', this.handleScroll, { passive: true })
    target.addEventListener('wheel', this.handleWheel, { passive: false })
    target.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    target.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    window.addEventListener('resize', this.handleResize, { passive: true })

    // Also listen for wheel events directly on the footer to prevent internal scrolling during animation
    this.element.addEventListener('wheel', this.handleFooterWheel, { passive: false })

    // Listen for footer's internal scroll events to manage touch state
    this.element.addEventListener('scroll', this.handleFooterScroll, { passive: true })

    // Initialize footer state
    this.footerProgress = 0 // 0 = hidden, 1 = fully visible
    this.isAtBottom = false
    this.lastTouchY = null

    // Set up initial state
    this.initializeWithRetry()
  }

  initializeWithRetry() {
    let attempts = 0
    const maxAttempts = 5

    const tryInitialize = () => {
      // Check if controller is still connected
      if (!this.element.isConnected) return

      attempts++

      if (this.canCalculateDimensions()) {
        this.updateParallax()
      } else if (attempts < maxAttempts) {
        this.initTimeout = setTimeout(tryInitialize, 100 * attempts) // Increasing delay
      }
    }

    // Start first attempt immediately, then with delays if needed
    this.initTimeout = setTimeout(tryInitialize, 50)
  }

  isMenuOpen() {
    return document.querySelector('aside.menu-overlay.open') !== null
  }

  canCalculateDimensions() {
    const { containerHeight } = this.getDimensions()
    const maxScrollTop = this.getMaxScrollTop()
    return containerHeight > 0 && maxScrollTop > 0
  }

  disconnect() {
    // Clear any pending timeouts to prevent memory leaks
    clearTimeout(this.initTimeout)
    clearTimeout(this.resizeTimeout)

    const target = (this.scrollElement === window ? window : this.scrollElement)
    if (target) {
      target.removeEventListener('scroll', this.handleScroll)
      target.removeEventListener('wheel', this.handleWheel)
      target.removeEventListener('touchstart', this.handleTouchStart)
      target.removeEventListener('touchmove', this.handleTouchMove)
    }
    window.removeEventListener('resize', this.handleResize)
    this.element.removeEventListener('wheel', this.handleFooterWheel)
    this.element.removeEventListener('scroll', this.handleFooterScroll)
  }

  handleResize() {
    // Recalculate on resize with a small delay to ensure dimensions are updated
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      this.updateParallax()
    }, 150)
  }

  handleScroll() {
    if (this.isMenuOpen()) return
    // Throttle scroll events for performance
    if (!this.ticking) {
      requestAnimationFrame(() => {
        this.updateParallax()
        this.ticking = false
      })
      this.ticking = true
    }
  }

  updateParallax() {
    const scrollTop = this.getScrollTop()
    const maxScrollTop = this.getMaxScrollTop()
    
    // Check if we're at the bottom of content (within 5px tolerance)
    this.isAtBottom = scrollTop >= (maxScrollTop - 5)
    
    if (!this.isAtBottom) {
      // Not at bottom - hide footer and reset progress
      this.footerProgress = 0
      this.updateFooterTransform(100) // 100% = hidden
      this.lastTouchY = null // Reset touch tracking
    } else {
      // At bottom - show footer based on current progress
      const translatePercent = (1 - this.footerProgress) * 100
      this.updateFooterTransform(translatePercent)
    }

    // Manage footer scrolling class based on progress
    this.updateFooterScrollState()
  }

  handleWheel(event) {
    if (this.isMenuOpen()) return
    // Only handle wheel events when at bottom
    if (!this.isAtBottom) return

    // Prevent default scrolling when footer is active
    if (this.footerProgress > 0) {
      event.preventDefault()
    }

    this.handleWheelForFooter(event)
  }

  handleFooterWheel(event) {
    if (this.isMenuOpen()) return
    // When mouse is over footer prevent default scrolling so that the main content can continue to scroll
    if (this.footerProgress < 1) {
      // Footer not fully visible. Intercept all wheel events for animation
      event.preventDefault()
      event.stopPropagation()
      this.handleWheelForFooter(event)
    } else {
      // Footer is fully visible. Check if we should hide it
      const deltaY = event.deltaY
      const isScrollingUp = deltaY < 0
      const isFooterAtTop = this.element.scrollTop <= 1 // Allow 1px tolerance

      if (isScrollingUp && isFooterAtTop) {
        // User scrolling up and footer content is at top. Start hiding footer
        event.preventDefault()
        event.stopPropagation()
        this.handleWheelForFooter(event)
      }
      // Otherwise, allow normal footer internal scrolling
    }
  }

  handleWheelForFooter(event) {
    const deltaY = event.deltaY
    const sensitivity = 0.002

    if (deltaY > 0) {
      // Scrolling down - show more footer
      this.footerProgress = Math.min(this.footerProgress + (deltaY * sensitivity), 1)
    } else if (deltaY < 0) {
      // Scrolling up - hide footer
      this.footerProgress = Math.max(this.footerProgress + (deltaY * sensitivity), 0)

      // If footer is fully hidden, allow normal scrolling again
      if (this.footerProgress === 0) {
        this.isAtBottom = false
        // Reset footer scroll position to top for next time
        this.element.scrollTop = 0
        return
      }
    }

    // Update footer position
    const translatePercent = (1 - this.footerProgress) * 100
    this.updateFooterTransform(translatePercent)
    this.updateFooterScrollState()
  }

  handleFooterScroll() {
    // Reset touch tracking when footer scrolls internally
    // This ensures clean state when user scrolls footer content then tries to hide footer
    this.lastTouchY = null
  }

  updateFooterScrollState() {
    // Enable footer internal scrolling only when fully visible
    if (this.footerProgress >= 1) {
      this.element.classList.add('footer-fully-visible')
    } else {
      this.element.classList.remove('footer-fully-visible')
    }
  }

  // Helper method to update footer transform with a specific translate percentage
  updateFooterTransform(translatePercent) {
    this.element.style.transform = `translateY(${translatePercent}%)`
  }

  handleTouchStart(event) {
    if (this.isMenuOpen()) return
    this.lastTouchY = event.touches[0].clientY
  }

  handleTouchMove(event) {
    if (this.isMenuOpen()) return
    // Handle touch events for footer animation
    if (this.lastTouchY === null) return

    const currentY = event.touches[0].clientY
    const deltaY = this.lastTouchY - currentY // Positive = swipe up, negative = swipe down
    const sensitivity = 0.005 // Adjust for touch sensitivity

    // Check if we should handle this touch event
    let shouldHandleTouch = false

    if (!this.isAtBottom) {
      this.lastTouchY = currentY
      return
    }

    if (this.footerProgress < 1) {
      // Footer not fully visible. Always handle
      shouldHandleTouch = true
    } else {
      // Footer fully visible. Only handle if swiping down and footer content is at top
      const isSwipingDown = deltaY < 0
      const isFooterAtTop = this.element.scrollTop <= 1
      shouldHandleTouch = isSwipingDown && isFooterAtTop
    }

    if (shouldHandleTouch) {
      event.preventDefault()

      if (deltaY > 0) {
        // Swiping up. Show more footer
        this.footerProgress = Math.min(this.footerProgress + (deltaY * sensitivity), 1)
      } else if (deltaY < 0) {
        // Swiping down. Hide footer
        this.footerProgress = Math.max(this.footerProgress + (deltaY * sensitivity), 0)

        // If footer is fully hidden, allow normal scrolling again
        if (this.footerProgress === 0) {
          this.isAtBottom = false
          this.lastTouchY = null
          // Reset footer scroll position to top for next time
          this.element.scrollTop = 0
          return
        }
      }

      // Update footer position
      const translatePercent = (1 - this.footerProgress) * 100
      this.updateFooterTransform(translatePercent)
      this.updateFooterScrollState()
    }
    
    this.lastTouchY = currentY
  }
  
  getMaxScrollTop() {
    if (this.scrollElement === window) {
      return document.documentElement.scrollHeight - window.innerHeight
    }
    return this.scrollElement.scrollHeight - this.scrollElement.clientHeight
  }

  getDimensions() {
    let containerHeight
    
    if (this.scrollElement === window) {
      containerHeight = window.innerHeight
    } else {
      containerHeight = this.scrollElement.clientHeight
    }
    
    return { containerHeight }
  }

  getScrollTop() {
    if (this.scrollElement && this.scrollElement !== window) {
      return this.scrollElement.scrollTop || 0
    }
    return window.pageYOffset || document.documentElement.scrollTop || 0
  }
}


